<template>
    <b-container fluid class="p-0">
        <situacao-licitacao :idLicitacao="licitacao.IdLicitacao" />
        <b-card class="mx-3" no-body>
            <template #header>
                <h6 class="mb-0 text-center">
                    <span class="titulo-sagres font-weight-bold">{{ tituloPagina }}</span>
                    <btn-voltar />
                    <modal-verificar-licitacao
                        v-if="licitacao.IdLicitacao && licitacao.getSituacao() === 'Em cadastramento'"
                        :idLicitacao="licitacao.IdLicitacao" />
                </h6>
            </template>
            <b-tabs v-model="abaAtiva" card fill>
                <b-tab>
                    <template #title>
                        <font-awesome-icon icon="pencil" class="mr-1 text-info" v-b-tooltip.hover title="Dados Iniciais" />
                        <span class="d-none d-md-inline">Dados iniciais</span>
                    </template>
                    <aba-editar-dados-iniciais v-if="showForm && abaAtiva === 0" @atualizarLicitacao="atualizarLicitacao"
                        :lic="licitacao"></aba-editar-dados-iniciais>
                </b-tab>
                <b-tab v-if="isEdit" lazy>
                    <template #title>
                        <font-awesome-icon icon="shopping-cart" class="mr-1 text-primary" v-b-tooltip.hover
                            :title="textoLoteItem" />
                        <span class="d-none d-md-inline">{{ textoLoteItem }}</span>
                    </template>
                    <!-- um componente para edição de lotes, outro para edição de itens-->
                    <span v-if="licitacao.IsLote">
                        <aba-editar-lotes :lic="licitacao"></aba-editar-lotes>
                    </span>
                    <aba-editar-itens v-else :lic="licitacao"></aba-editar-itens>
                </b-tab>
                <b-tab v-if="isEdit" lazy>
                    <template #title>
                        <font-awesome-icon icon="address-card" class="mr-1 text-success" v-b-tooltip.hover
                            title="Participantes" />
                        <span class="d-none d-md-inline">Participantes</span>
                    </template>
                    <aba-editar-participantes :lic="licitacao"></aba-editar-participantes>
                </b-tab>
                <b-tab v-if="isEdit" lazy>
                    <template #title>
                        <font-awesome-icon icon="trophy" class="mr-1 text-warning" v-b-tooltip.hover title="Vencedores" />
                        <span class="d-none d-md-inline">Vencedores</span>
                    </template>
                    <aba-editar-vencedores v-if="abaAtiva === 3" @atualizarLicitacao="atualizarLicitacao"
                        :lic="licitacao"></aba-editar-vencedores>
                </b-tab>
                <b-tab v-if="isEdit" class="p-0" lazy>
                    <template #title>
                        <font-awesome-icon icon="hourglass-end" class="mr-1 text-secondary" v-b-tooltip.hover
                            title="Histórico" />
                        <span class="d-none d-md-inline">Histórico</span>
                    </template>
                    <detalhes-historico :idLicitacao="licitacao.IdLicitacao"></detalhes-historico>
                </b-tab>
            </b-tabs>
        </b-card>
    </b-container>
</template>

<script lang="ts">
import DetalhesHistorico from '@/components/licitacoes/ldi/DetalhesHistorico.vue'
import SituacaoLicitacao from '@/components/licitacoes/ldi/SituacaoLicitacao.vue'
import AbaEditarDadosIniciais from '@/components/licitacoes/ldi/editar-ldi/AbaEditarDadosIniciais.vue'
import AbaEditarItens from '@/components/licitacoes/ldi/editar-ldi/AbaEditarItens.vue'
import AbaEditarLotes from '@/components/licitacoes/ldi/editar-ldi/AbaEditarLotes.vue'
import AbaEditarParticipantes from '@/components/licitacoes/ldi/editar-ldi/AbaEditarParticipantes.vue'
import AbaEditarVencedores from '@/components/licitacoes/ldi/editar-ldi/AbaEditarVencedores.vue'
import BtnVoltar from '@/components/shared/BtnVoltar.vue'
import { eventBus } from '@/main'
import ldiService from '@/services/licitacoes/ldiService'
import { store } from "@/store"
import { Licitacao } from '@/types/licitacoes/ldi/licitacao'
import { UtilsTS } from '@/utilsts'
import { mixins } from 'vue-class-component'
import { Component, Vue } from 'vue-property-decorator'
import ModalVerificarLicitacao from '@/components/licitacoes/ldi/modais/ModalVerificarLicitacao.vue'

const CompProps = Vue.extend({
    name: 'EditarLicitacao',
    components: {
        AbaEditarDadosIniciais, AbaEditarItens, AbaEditarLotes, AbaEditarParticipantes, AbaEditarVencedores,
        BtnVoltar, DetalhesHistorico, ModalVerificarLicitacao, SituacaoLicitacao
    },
    props: {
        idLicitacao: {
            type: String,
            default: ''
        }
    }
})

@Component
export default class EditarLicitacao extends mixins(UtilsTS, CompProps) {
    licitacao: Licitacao = new Licitacao()
    isEdit = false
    abaAtiva = 0 // qual aba está ativa no momento
    showForm = false

    get tituloPagina(): string {
        return `${this.isEdit ? 'Editar' : 'Criar'} Licitação ${this.isEdit ? this.licitacao.NumeroAnoLicitacao : ''}`
    }

    get mostrarBtnSave(): boolean {
        return this.abaAtiva === 0
    }

    get textoLoteItem(): string {
        if (this.licitacao) return this.licitacao.IsLote ? 'Lotes' : 'Itens'
        return 'Lotes/Itens'
    }

    async created() {
        try {
            store.blockScreen = true
            if (this.idLicitacao.length > 0) {
                this.isEdit = true
                this.licitacao = await ldiService.buscarDadosGerais(this.idLicitacao)
            }
        }
        catch (e: any) {
            console.error('Erro ao abrir a edição de licitações', e)
        } finally {
            this.showForm = true
            store.blockScreen = false
        }
    }

    /**
     * Vamos tentar centralizar aqui as mudanças em flags de controle e no objeto principal 'licitacao'
     * @param li nova instância de licitação, recém recebida da API
     */
    async atualizarLicitacao(li?: Licitacao): Promise<void> {
        store.blockScreen = true
        try {
            // console.log('editarlicitacao.atualizarLicitacao recebeu ', li, this.idLicitacao, this.licitacao)
            if (!li) {
                const parametro = String(this.idLicitacao && this.idLicitacao !== '' ? this.idLicitacao : this.licitacao.IdLicitacao)
                li = await ldiService.buscarDadosGerais(parametro)
            }
            this.licitacao = Object.assign(new Licitacao(), li)
            eventBus.$emit('atualizar-status-licitacao', this.licitacao.FlSituacao)
            // console.log('editarLicitacao.atualizarLicitacao nova instancia de licitacao local', this.licitacao)
            if (li.IdLicitacao > 0) this.isEdit = true
        } finally {
            store.blockScreen = false

        }
    }
}
</script>

<style></style>